import { CircleSlashIcon } from 'lucide-react';

import { THRESHOLD_LABEL, THRESHOLD_TEXT } from '@/features/workspace-usage';
import { isReached, type LimitState } from '../limitState';

export interface WorkflowLimitMarkProps {
  workflowName: string;
  /** This workflow's own state, never the workspace's. */
  state: LimitState;
}

/**
 * Which board is stopped, without opening each one (spec 101 US4).
 *
 * Only `reached`, and only this workflow's own limit. The list answers a single
 * question at a glance, and a second state in it would make that glance
 * ambiguous — approaching belongs in the top bar, where there is room to say
 * which state it is. A workflow with no cap of its own is never marked, whatever
 * the workspace is doing: the workspace's state is already in the top bar, and
 * repeating it against every row would say nothing about any of them (FR-019).
 *
 * Marked from a refused run as well as from the figures, unlike the workspace
 * marker. The question here is whether that board will run, and a run the
 * service has already refused answers it for anyone who can see the board.
 *
 * The glyph is never alone: the row carries the workflow's name and its state in
 * words for anyone not reading colour or shape (FR-020, FR-021).
 */
export function WorkflowLimitMark({
  workflowName,
  state,
}: WorkflowLimitMarkProps) {
  if (!isReached(state)) return null;

  return (
    <>
      <CircleSlashIcon
        aria-hidden="true"
        className={`${THRESHOLD_TEXT.exhausted} mt-0.5 size-3.5 shrink-0`}
        strokeWidth={1.5}
      />
      <span className="sr-only">
        {workflowName}: {THRESHOLD_LABEL.exhausted}
      </span>
    </>
  );
}
